import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, styled, Typography } from '@mui/material'
import type { FC, PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, remUnit, unit } from '@hcr/ui'
import { isNotNull } from '@hcr/utils'

import type { TicketsCount } from '../../../../../hooks/common'
import { Path } from '../../../../../models'
import { LayoutBookingOverviewHero } from '../layout-booking-overview'

interface BookingOverviewHeroCardProps {
  linkLabel: ReactNode
  onLinkClick?: () => void
  ticketsCount: TicketsCount | null
  title: ReactNode
  totalTickets: number
}

export const BookingOverviewHeroCard: FC<PropsWithChildren<BookingOverviewHeroCardProps>> = ({
  children,
  linkLabel,
  onLinkClick,
  ticketsCount,
  title,
  totalTickets,
}) => {
  const { t } = useTranslation()

  return (
    <LayoutBookingOverviewHero>
      <Stack direction='row' gap={unit(3)}>
        <Stack height={unit(6.5)} alignItems={'center'} flexDirection='row'>
          <FontAwesomeIcon fontSize={remUnit(5)} icon={ICONS.falCalendarHeart} color={COLORS.burgundi[500]} />
        </Stack>
        <Stack direction='column' paddingRight={unit(3)}>
          <Title variant='titleS'>{title}</Title>
          <Stack direction='column' paddingRight={unit(10.5)}>
            <BookingInfo variant='bodyM'>{children}</BookingInfo>
            {isNotNull(ticketsCount) && totalTickets > 0 && (
              <TicketsInfo variant='bodyM' fontWeight='initial' color={COLORS.black}>
                <FontAwesomeIcon color={COLORS.black} icon={ICONS.farLayerGroup} style={{ marginRight: unit(1) }} />
                {totalTickets > 1
                  ? t('utils.tickets-count-plural', { count: totalTickets })
                  : t('utils.tickets-count', { count: totalTickets })}
              </TicketsInfo>
            )}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- renders a real <a href>, so Enter already triggers onClick natively */}
            <Typography
              component='a'
              rel={'rel'}
              href={Path.Booking}
              role='link'
              tabIndex={0}
              variant='textLinkM'
              color={COLORS.burgundi[500]}
              onClick={onLinkClick}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: unit(1.5),
              }}
            >
              {linkLabel}
              <FontAwesomeIcon icon={ICONS.farArrowRight} />
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </LayoutBookingOverviewHero>
  )
}

const Title = styled(Typography)`
  letter-spacing: 0;
  line-height: ${remUnit(6.5)};
`

const BookingInfo = styled(Typography)`
  letter-spacing: 0;
`

const TicketsInfo = styled(Typography)`
  letter-spacing: 0;
  white-space: nowrap;
`
